import { useState, useEffect, useCallback } from "react";

export type MicrophonePermissionState =
  | "prompt"
  | "granted"
  | "denied"
  | "unsupported";

interface UseMicrophonePermissionReturn {
  permissionState: MicrophonePermissionState;
  isChecking: boolean;
  requestPermission: () => Promise<boolean>;
  recheckPermission: () => Promise<void>;
}

/**
 * Hook to check and request microphone permissions
 * Works with both React and Preact
 */
export function useMicrophonePermission(): UseMicrophonePermissionReturn {
  const [permissionState, setPermissionState] =
    useState<MicrophonePermissionState>("prompt");
  const [isChecking, setIsChecking] = useState(true);

  const checkPermissionState = useCallback(async () => {
    if (!navigator.permissions || !navigator.permissions.query) {
      setPermissionState("unsupported");
      setIsChecking(false);
      return;
    }

    try {
      const result = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });

      setPermissionState(result.state as MicrophonePermissionState);

      try {
        result.addEventListener("change", () => {
          setPermissionState(result.state as MicrophonePermissionState);
        });
      } catch {
        // Permission change listener not supported
      }

      setIsChecking(false);
    } catch {
      setPermissionState("unsupported");
      setIsChecking(false);
    }
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setPermissionState("granted");
      return true;
    } catch {
      setPermissionState("denied");
      return false;
    }
  }, []);

  const recheckPermission = useCallback(async () => {
    setIsChecking(true);
    await checkPermissionState();
  }, [checkPermissionState]);

  useEffect(() => {
    checkPermissionState();
  }, [checkPermissionState]);

  // Poll for permission changes (Safari fallback)
  useEffect(() => {
    if (permissionState === "unsupported") return;

    const pollInterval = setInterval(async () => {
      if (!navigator.permissions || !navigator.permissions.query) return;

      try {
        const result = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });
        if (result.state !== permissionState) {
          setPermissionState(result.state as MicrophonePermissionState);
        }
      } catch {
        // Silently fail
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [permissionState]);

  return {
    permissionState,
    isChecking,
    requestPermission,
    recheckPermission,
  };
}
